/**
 * The agents domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted in
 * the flags whole-class fan-out).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/agents/flags` publishes both.
 * `/contributions` carries nothing for flags — this domain HAS such a door,
 * holding the Work analytics event names, and none of the declarations below
 * belongs in it.
 *
 * THIS IS A DOOR BIRTH, not a top-up, and it is the whole of what lifts this
 * package's flag ban. `packages/agents/src/manifest-boundary.test.ts` refused
 * `@heidi/runtime/feature-flags` in `/ui` with the why-string "banned until this
 * zone declares its getter module"; the condition is now met, so the row's
 * reason is rewritten rather than deleted — the ban still holds for every file
 * that is not this one, in `/ui` and in `/data` alike, because
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` scopes the permission to
 * the DECLARED module rather than to a zone. `packageUi.agents.flags.disposition`
 * stays `'none'`: that field records a flag PORT (a resolved boolean crossing as
 * a prop, ruling 36 (b)), which this change does not create.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration above it, so the pair cannot drift, and `flags.test.ts`
 * asserts each key appears exactly once in this file and that no read takes a
 * string literal — the only assertion that can tell a descriptor read from a
 * hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard of their own (cache keys do), so this file's source
 * case and `scripts/check-flag-getter-doors.test.ts` are the whole of what
 * stands between a key and a second spelling.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection. The walk
 * in `check-flag-getter-doors.test.ts` crosses package boundaries and refuses
 * `@heidi/design-system`, this package's own `/ui` zone and the app tree
 * anywhere in the graph, and separately proves the door imports in a bare Node
 * process with no DOM and no side effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/agents/flags`, so its importers and their
 * `jest.mock` literals are untouched.
 *
 * WHAT DOES NOT BELONG HERE — FOURTEEN of this domain's seventeen app-barrel
 * names are NOT below, and each is named rather than silently skipped:
 *
 *   - The Heidi Work umbrella `useIsProjectsEnabled` and the getters that follow
 *     it (`useIsWorkflowsEnabled`, `useIsSessionRoutineRunsEnabled`,
 *     `useIsAgentSessionRecordingEnabled`,
 *     `useIsProjectsContextualLoaderPhrasesEnabled`,
 *     `useIsWorkLetterArtifactEnabled`) live in
 *     `src/features/flags/heidi-work-umbrella.ts`. The umbrella ORs PostHog
 *     `release-enable-heidi-ii`, which this door cannot read; only the Projects
 *     descriptors are declared here. `@heidi/agents/data` receives the resolved
 *     answer through `configureAgentsData`.
 *   - `useIsRpaAgentEnabled`, `useIsCmdShiftHOverlayEnabled` and
 *     `useIsRpaRecorderEnabled` belong to the RPA root, which the 2026-08-21
 *     roots ruling puts app-shell. The first composes `useIsProjectsEnabled`
 *     from the app side and the other two compose IT.
 *   - `useIsVoiceDictationModeEnabled` and `useIsAskHeidiSourcesShortcutsEnabled`
 *     resolve through `useFeatureFlagEnabled` from `@/features/flags/posthog-compat`,
 *     which reads posthog-js on cloud and only falls back to OpenFeature on the
 *     airgapped build. `@heidi/runtime/feature-flags` carries no PostHog surface,
 *     so moving one would change WHICH PROVIDER answers — a behaviour change
 *     wearing a refactor's clothes, and `monorepo-wave-1-p3-feature-flags` Z4 is
 *     the standing rule against it.
 *   - `useIsWorkInSessionEnabled` has no agents-root or package-UI reader and its
 *     noun is ambiguous against session, so ownership is unmeasured.
 *   - `useIsNangoIntegrationsEnabled` is integrations-owned and reads a bespoke
 *     `NEXT_PUBLIC_NANGO_FORCE_ENABLE` the door's `forceEnableAll` does not
 *     reproduce.
 *   - `useIsWorkHomeIterationEnabled` stays on the app barrel. It reads PostHog
 *     through `useFeatureFlagEnabled`; this door has no PostHog surface, and
 *     `WorkHomeScene` takes the resolved boolean as a prop.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
  useStringFlag,
} from '@heidi/runtime/feature-flags';
// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on twenty-four of the twenty-seven descriptors, measured rather than assumed. The
// surfaces these gate are the Work/Projects routes (`/scribe/projects`), the
// routine-run panels, the project run-options selector, Suggested work's
// blended jobs, the Work home mark, owned routines opening on their latest
// run, the action row on earlier Work replies, Routines event-rule authoring,
// and the Talk-to-Heidi voice widget and its workspace window — and not one of
// them carries an `isOnPrem` gate.
// Omitting the field would silently take the D6 default (`'cloud-only'`) and
// withhold the whole Work surface from the appliance. The two exceptions are
// declared `'cloud-only'` on purpose: `ambientHeidiFlag` (desktop-only wake
// word) and `workMobileHandoffCtaFlag` (the appliance has no push client and
// does not mount the get-mobile modal).
// ---------------------------------------------------------------------------

/**
 * The DESKTOP arm of the Projects/Heidi Work umbrella: persistent
 * harness-backed agent runs with Evidence tools, computer-use and scheduling.
 * Enabling it also implies Routines and the desktop RPA / Computer-Use agent —
 * there is no separate RPA kill switch, and ops kill desktop RPA by turning this
 * umbrella off.
 */
export const projectsFlag = defineFeatureFlag({
  key: 'release-scribe-enable-projects',
  defaultValue: false,
  description: 'Heidi Work / Projects umbrella on desktop',
  builds: 'all',
});

/**
 * The WEB arm of the same umbrella, ramped on its own key so flipping the
 * desktop master never exposes web. There is no desktop client on web, so the
 * backend strips `run_computer_use_task` and the surface runs as an
 * Evidence/clinical orchestration agent.
 */
export const projectsWebFlag = defineFeatureFlag({
  key: 'release-scribe-enable-projects-web',
  defaultValue: false,
  description: 'Heidi Work / Projects umbrella on web',
  builds: 'all',
});

/**
 * The experimental run-options selector shown only on project creation
 * surfaces. The selected options are persisted on the created project by
 * ml-scribe as `run_flags`.
 */
export const projectRunOptionsFlag = defineFeatureFlag({
  key: 'release-scribe-enable-project-run-options',
  defaultValue: false,
  description: 'Run-options selector on project creation surfaces',
  builds: 'all',
});

export const workLetterArtifactFlag = defineFeatureFlag({
  key: 'release-work-letter-artifact',
  defaultValue: false,
  description: 'Auto-open agent-created letters in the Work canvas',
  builds: 'all',
});

/**
 * Legacy session-routine key retained for compatibility with flag inventories.
 * Runtime enablement now follows the platform-specific Projects umbrella.
 */
export const sessionRoutineRunsFlag = defineFeatureFlag({
  key: 'release-scribe-session-routine-runs',
  defaultValue: false,
  description: 'Session-scoped routine-run queries',
  builds: 'all',
});

export const workHomeMarkFlag = defineFeatureFlag({
  key: 'release-scribe-work-home-mark',
  defaultValue: false,
  description: 'Directional Work home mark',
  builds: 'all',
});

/** Rollout gate for Direction C on the Heidi II Work homepage. */
export const workHomeDirectionCTaxonomyFlag = defineFeatureFlag({
  key: 'release-scribe-work-home-direction-c-taxonomy',
  defaultValue: false,
  description: 'Direction C starter taxonomy on Heidi II Work home',
  builds: 'all',
});

/**
 * Live-grant sharing of Work threads. Layers on the Projects umbrella.
 * Default OFF until B3/B5/B9 and the F2 read-only view ship together.
 */
export const projectSharingFlag = defineFeatureFlag({
  key: 'release-scribe-enable-project-sharing',
  defaultValue: false,
  description: 'Share a Work thread with another clinician as view-only',
  builds: 'all',
});

/**
 * "Duplicate to my Work" for a thread the user already owns. Independent of
 * sharing so product can expose copy without live grants.
 */
export const projectDuplicateFlag = defineFeatureFlag({
  key: 'release-scribe-enable-project-duplicate',
  defaultValue: false,
  description: "Duplicate a Work thread into the current user's Work",
  builds: 'all',
});

/**
 * Grantee CTA to fork a shared thread into their own Work copy.
 */
export const projectContinueFlag = defineFeatureFlag({
  key: 'release-scribe-enable-project-continue',
  defaultValue: false,
  description: "Continue a shared Work thread in the grantee's own copy",
  builds: 'all',
});

/**
 * THE ROUTINES "YOURS" VS "DISCOVER" LANDING-TAB THRESHOLD — a rollout dial,
 * not an Amplitude experiment, so no `useManualExposure` pairing.
 *
 * NOT a `defineFeatureFlag` descriptor: `defaultValue` there is typed
 * `boolean` (`packages/runtime/src/shell/contributions.ts`) and
 * `isFeatureFlagContribution` refuses a non-boolean default, so a STRING
 * default has nowhere to be declared — see ".ai-context/feature-flags-in-packages.md",
 * "A STRING flag cannot move yet". The key is declared as a plain constant
 * instead, minted exactly once, here, and read by the getter directly below —
 * the same one-minting-site property the descriptors above get from
 * co-location.
 */
export const ROUTINE_BROWSE_YOURS_THRESHOLD_FLAG_KEY =
  'config-routines-browse-yours-threshold';

/**
 * Fallback string kept in sync with `DEFAULT_ROUTINE_BROWSE_YOURS_THRESHOLD`
 * in `data/workflows/lib/routine-browse-filter.ts` (not imported — that file
 * is a separate lib and this door stays a single, dependency-free literal set,
 * same as every other descriptor above).
 */
const ROUTINE_BROWSE_YOURS_THRESHOLD_FLAG_DEFAULT = '2';

/**
 * Post-session CDI nudges DEMO (RCM-1151) — a JSON string flag,
 * `{"mode":"closed"|"open","routineTitle"?:string}`. A plain key constant for
 * the same reason as the threshold above: the default is a string. Parsed
 * app-side by `parseCdiNudgesDemoConfig`. Kill gate only, no exposure.
 */
export const CDI_NUDGES_DEMO_FLAG_KEY = 'release-scribe-cdi-nudges-demo';

/**
 * THE KEY, PUBLISHED — and derived rather than declared.
 *
 * The app barrel exported this constant before the move and a conversion leaves
 * the barrel's export surface intact, so the name cannot simply vanish.
 * Re-declaring the literal in the barrel would be a SECOND MINTING SITE, which
 * is the one thing ruling 36 exists to refuse, and neither side's own suite can
 * see that: this file's mint-count case reads only this source, and the app's
 * suite asserts a VALUE, which a copy satisfies. What refuses it is
 * `scripts/check-flag-getter-doors.test.ts` ('a declared key is minted ONCE, on
 * both sides of the door'). So the descriptor above stays the single minting
 * site and this reads `key` off it — the `MEDICATION_HIGHLIGHTS_FLAG_KEY`
 * precedent.
 */
export const SESSION_ROUTINE_RUNS_FLAG_KEY = sessionRoutineRunsFlag.key;

/**
 * The global "Talk to Heidi" voice assistant — the floating button, the modal,
 * and the workspace window and its placement. Same key the backend WS route
 * gates on. When off, the widget never mounts.
 */
export const talkToHeidiFlag = defineFeatureFlag({
  key: 'release-scribe-talk-to-heidi',
  defaultValue: false,
  description: 'Talk to Heidi voice assistant, workspace window and placement',
  builds: 'all',
});

/**
 * THE KEY, PUBLISHED — same derivation as above. It was exported from
 * `src/features/voice-assistant/workspace/workspaceFlag.ts`, which is now a
 * re-export shim over this module, so the name keeps resolving from its old path
 * for the two suites that mock it.
 */
export const TALK_TO_HEIDI_FLAG_KEY = talkToHeidiFlag.key;

/**
 * Desktop-only on-device "Hey Heidi" listener. Default OFF. cloud-only so
 * the on-prem appliance never receives the flag; the host also self-gates
 * with `isTauri()`.
 */
export const ambientHeidiFlag = defineFeatureFlag({
  key: 'release-scribe-ambient-heidi',
  defaultValue: false,
  description: 'On-device Hey Heidi ambient listener for Talk to Heidi',
  builds: 'cloud-only',
});

/**
 * Talk to Heidi hands computer tasks to a background Work agent. Default OFF.
 * The client half only: when on, the desktop advertises its computer-use
 * capabilities to the voice server and submits the turns it is handed. The
 * server's own kill switch must also be on for the tool to be offered.
 */
export const voiceComputerUseFlag = defineFeatureFlag({
  key: 'release-scribe-voice-computer-use',
  defaultValue: false,
  description: 'Talk to Heidi: hand computer tasks to a background Work agent',
  builds: 'cloud-only',
});

/**
 * Owned routines open on their latest run with the routine card available.
 * Default-on because this is a rollback switch for the shipped landing model,
 * not a staged rollout.
 */
export const routinesOwnedOpenLastRunFlag = defineFeatureFlag({
  key: 'op-routines-owned-open-last-run',
  defaultValue: true,
  description: 'Routines: open owned routines on their latest run',
  builds: 'all',
});

export const useIsRoutinesOwnedOpenLastRunEnabled = (): boolean =>
  useBooleanFlag(
    routinesOwnedOpenLastRunFlag.key,
    routinesOwnedOpenLastRunFlag.defaultValue
  );

/**
 * The response action row (thumbs + copy) on EARLIER Work replies, not only the
 * latest. Default-on because this is a rollback switch for shipped behaviour,
 * not a staged rollout; off restores the latest-reply-only row.
 */
export const workEarlierResponseActionsFlag = defineFeatureFlag({
  key: 'op-work-earlier-response-actions',
  defaultValue: true,
  description: 'Work: response action row on earlier replies',
  builds: 'all',
});

export const useIsWorkEarlierResponseActionsEnabled = (): boolean =>
  useBooleanFlag(
    workEarlierResponseActionsFlag.key,
    workEarlierResponseActionsFlag.defaultValue
  );

/**
 * The "Continue on mobile" card shown in Work chat while a run waits on an
 * approval, so a clinician without the app can install it and resolve the next
 * one from a push. Cloud-only: the appliance wires the no-op push client and
 * does not mount the get-mobile modal the card opens.
 */
export const workMobileHandoffCtaFlag = defineFeatureFlag({
  key: 'release-agents-mobile-handoff-cta',
  defaultValue: false,
  description: 'Work: "Continue on mobile" card on pending approvals',
  builds: 'cloud-only',
});

export const useIsWorkMobileHandoffCtaEnabled = (): boolean =>
  useBooleanFlag(workMobileHandoffCtaFlag.key, workMobileHandoffCtaFlag.defaultValue);

/**
 * Cursor-style ghost composer queue (collapse + borderless rows). Default-on
 * kill switch — off restores the bordered card list with Clear all so Evidence
 * and Work can roll back without a revert deploy.
 */
export const composerGhostQueueFlag = defineFeatureFlag({
  key: 'op-composer-ghost-queue',
  defaultValue: true,
  description: 'Composer: Cursor-style ghost queued-message list',
  builds: 'all',
});

export const useIsComposerGhostQueueEnabled = (): boolean =>
  useBooleanFlag(composerGhostQueueFlag.key, composerGhostQueueFlag.defaultValue);

/**
 * Work-surface kill switch for the focused artifact pane. Independent of
 * Session Layout V2, so Work users on the legacy session page can still get it.
 * Ships on the appliance: the app getter had no on-prem gate.
 */
export const workSessionArtifactV2Flag = defineFeatureFlag({
  key: 'release-scribe-work-session-artifact-v2',
  defaultValue: false,
  description: 'Work: focused artifact pane on the session surface',
  builds: 'all',
});

export const useIsWorkSessionArtifactV2Enabled = (): boolean =>
  useBooleanFlag(workSessionArtifactV2Flag.key, workSessionArtifactV2Flag.defaultValue);

/** Work composer Fast/Normal speed row. Fast sends run_flags.fast_mode. */
export const workFastModeFlag = defineFeatureFlag({
  key: 'release-scribe-enable-work-fast-mode',
  defaultValue: false,
  description: 'Work composer Fast/Normal speed row',
  builds: 'all',
});

export const useIsWorkFastModeEnabled = (): boolean =>
  useBooleanFlag(workFastModeFlag.key, workFastModeFlag.defaultValue);

/** "Leave this chat?" confirm for an unsent Work composer draft. */
export const workDraftLeaveGuardFlag = defineFeatureFlag({
  key: 'release-scribe-work-draft-leave-guard',
  defaultValue: false,
  description: 'Leave-chat confirm for an unsent Work composer draft',
  builds: 'all',
});

export const useIsWorkDraftLeaveGuardEnabled = (): boolean =>
  useBooleanFlag(workDraftLeaveGuardFlag.key, workDraftLeaveGuardFlag.defaultValue);

/** First-call Evidence disclaimer inside Work, and its page mount. */
export const workEvidenceDisclaimerFlag = defineFeatureFlag({
  key: 'release-scribe-work-evidence-disclaimer',
  defaultValue: false,
  description: 'First-call Evidence disclaimer inside Work',
  builds: 'all',
});

export const useIsWorkEvidenceDisclaimerEnabled = (): boolean =>
  useBooleanFlag(workEvidenceDisclaimerFlag.key, workEvidenceDisclaimerFlag.defaultValue);

/** Compact, actionable Work source-citation chips. */
export const workSourceCitationChipsFlag = defineFeatureFlag({
  key: 'release-scribe-work-source-citation-chips',
  defaultValue: false,
  description: 'Compact Work source-citation chips',
  builds: 'all',
});

export const useIsWorkSourceCitationChipsEnabled = (): boolean =>
  useBooleanFlag(
    workSourceCitationChipsFlag.key,
    workSourceCitationChipsFlag.defaultValue
  );

/**
 * Master gate for Routines event-trigger authoring. Mirrors the backend flag of
 * the same key; off hides the whole "Run on events" section.
 */
export const eventTriggersFlag = defineFeatureFlag({
  key: 'release-ml-scribe-enable-event-triggers',
  defaultValue: false,
  description: 'Routines: event-trigger authoring',
  builds: 'all',
});

export const useIsEventTriggersEnabled = (): boolean =>
  useBooleanFlag(eventTriggersFlag.key, eventTriggersFlag.defaultValue);

/** Nested gate for the "Only run when…" semantic-condition field. */
export const workflowEventRuleConditionsFlag = defineFeatureFlag({
  key: 'release-scribe-event-rule-semantic-conditions',
  defaultValue: false,
  description: 'Routines: semantic conditions on event rules',
  builds: 'all',
});

export const useIsWorkflowEventRuleConditionsEnabled = (): boolean =>
  useBooleanFlag(
    workflowEventRuleConditionsFlag.key,
    workflowEventRuleConditionsFlag.defaultValue
  );

/**
 * Nested gate for the "While the session is transcribing" event type. Mirrors
 * the backend emit kill switch of the same key.
 */
export const sessionTranscribingEventsFlag = defineFeatureFlag({
  key: 'release-ml-scribe-session-transcribing-events',
  defaultValue: false,
  description: 'Routines: session-transcribing event type',
  builds: 'all',
});

/**
 * `false` is ambiguous: the flag is off, or the provider has not resolved yet.
 * Callers that persist or one-shot-seed from this value must pair the read with
 * the host's `useIsFlagProviderReady` and not treat an unresolved `false` as final.
 */
export const useIsSessionTranscribingEventsEnabled = (): boolean =>
  useBooleanFlag(
    sessionTranscribingEventsFlag.key,
    sessionTranscribingEventsFlag.defaultValue
  );

export const desktopTargetRuntimeFlag = defineFeatureFlag({
  key: 'release-scribe-enable-rpa-desktop-target-runtime',
  defaultValue: false,
  description: 'Gate native desktop-target runtime in cloud and on-prem builds',
  builds: 'all',
});

export const cuaCloudFlag = defineFeatureFlag({
  key: 'release-agents-cua-cloud',
  defaultValue: false,
  description: 'Cloud computer provisioning and resident setup',
  // Existing settings and selector readers consume this key from the on-prem bundle.
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// THE FORCE-ALL COLLAPSE, disclosed per getter rather than in aggregate. Four of
// the app-side bodies these replace carried a hand-rolled
// `process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true'` branch:
// `useIsProjectsEnabled`, `useIsSessionRoutineRunsEnabled` (both now app-side
// in `heidi-work-umbrella.ts`, still through `useBooleanFlag`),
// `useIsVoiceAssistantEnabled` and the non-React `isVoiceWorkspaceEnabled`;
// so did the three Routines event-rule getters above (`useIsEventTriggersEnabled`,
// `useIsWorkflowEventRuleConditionsEnabled`, `useIsSessionTranscribingEventsEnabled`).
// `useBooleanFlag` and `readFlag` return `forceEnableAll || value` from the
// boolean the app injects at `src/composition/wiring/runtime-wiring.ts` out of that same
// env var, so each branch COLLAPSES onto the door's own override rather than
// being dropped. The remaining getters GAIN that override, which a raw
// `useBooleanFlagValue` bypassed — the choke point the door exists for (design
// D8), and a dev-only knob. `flags.test.ts` asserts the ABSENCE of
// `process.env` here, so a later edit cannot reinstate a branch by hand.
// ---------------------------------------------------------------------------

/** The run-options selector on project creation surfaces. */
export const useIsProjectRunOptionsEnabled = (): boolean =>
  useBooleanFlag(projectRunOptionsFlag.key, projectRunOptionsFlag.defaultValue);

export const useIsWorkHomeMarkEnabled = (): boolean =>
  useBooleanFlag(workHomeMarkFlag.key, workHomeMarkFlag.defaultValue);

export const useIsWorkHomeDirectionCTaxonomyEnabled = (): boolean =>
  useBooleanFlag(
    workHomeDirectionCTaxonomyFlag.key,
    workHomeDirectionCTaxonomyFlag.defaultValue
  );

export const useIsProjectSharingEnabled = (): boolean =>
  useBooleanFlag(projectSharingFlag.key, projectSharingFlag.defaultValue);

export const useIsProjectDuplicateEnabled = (): boolean =>
  useBooleanFlag(projectDuplicateFlag.key, projectDuplicateFlag.defaultValue);

export const useIsProjectContinueEnabled = (): boolean =>
  useBooleanFlag(projectContinueFlag.key, projectContinueFlag.defaultValue);

/**
 * The RAW (unparsed) Routines landing-tab threshold. Parsing to an integer and
 * the fallback for a missing/non-numeric value live in the pure
 * `initialRoutineBrowseFilter` (`@heidi/agents/data`), so this getter stays a
 * plain string read — no `useManualExposure`, this is a rollout dial.
 */
export const useRoutineBrowseYoursThresholdRaw = (): string =>
  useStringFlag(
    ROUTINE_BROWSE_YOURS_THRESHOLD_FLAG_KEY,
    ROUTINE_BROWSE_YOURS_THRESHOLD_FLAG_DEFAULT
  );

/**
 * The RAW (unparsed) CDI nudges demo config; `''` when off. A string read
 * returns the value even when the flag is disabled (Flagsmith `getValue`), so
 * gate on the enabled state too. Both hooks run unconditionally.
 */
export const useCdiNudgesDemoRaw = (): string => {
  const enabled = useBooleanFlag(CDI_NUDGES_DEMO_FLAG_KEY, false);
  const raw = useStringFlag(CDI_NUDGES_DEMO_FLAG_KEY, '');
  return enabled ? raw : '';
};

/** The Talk to Heidi voice assistant — floating button and modal. */
export const useIsVoiceAssistantEnabled = (): boolean =>
  useBooleanFlag(talkToHeidiFlag.key, talkToHeidiFlag.defaultValue);

/** On-device Hey Heidi wake-word listener. Desktop only; default OFF. */
export const useIsAmbientHeidiEnabled = (): boolean =>
  useBooleanFlag(ambientHeidiFlag.key, ambientHeidiFlag.defaultValue);

/**
 * The same flag under the name the workspace chrome and placement code already
 * call. ON always means widget + window + placement together.
 */
export const useIsVoiceWorkspaceEnabled = (): boolean => useIsVoiceAssistantEnabled();

/**
 * The NON-REACT reader for the flag above, and ruling 36 (b)'s fetch-time shape
 * made concrete: its callers are the voice controller, the ui-action dispatch
 * and the store-wiring seam, none of which can run a hook.
 *
 * Before the move it lived at
 * `src/features/voice-assistant/workspace/workspaceFlag.ts` and built its OWN
 * `OpenFeature.getClient(FEATURE_FLAG_DOMAIN)` — a second place for the domain
 * constant to be got wrong, and a second minting site for the key. `readFlag`
 * resolves the same client on the same default domain, so the resolution path is
 * unchanged; what differs is the force-all collapse noted above.
 */
export const isVoiceWorkspaceEnabled = (): boolean =>
  readFlag(talkToHeidiFlag.key, talkToHeidiFlag.defaultValue);

/** Non-React reader for the voice controller, which runs no hooks. */
export const isVoiceComputerUseEnabled = (): boolean =>
  readFlag(voiceComputerUseFlag.key, voiceComputerUseFlag.defaultValue);

export const useIsDesktopTargetRuntimeEnabled = (): boolean =>
  useBooleanFlag(desktopTargetRuntimeFlag.key, desktopTargetRuntimeFlag.defaultValue);

export const useIsCuaCloudEnabled = (): boolean =>
  useBooleanFlag(cuaCloudFlag.key, cuaCloudFlag.defaultValue);
